import React, { useMemo, useCallback } from "react";
import { FlashList } from "@shopify/flash-list";
import { Box, Text, VStack, HStack, Pressable, Badge } from "native-base";
import { useAppContext } from "../../../../Universal/API/Context-API/AppContext";
import { defaultNavigationProps } from "../../../Navigator";

// ===== Types (можешь вынести в отдельный файл и импортировать) =====
type InstructionsBlock =
  | { type: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; style: "bullet" | "numbered"; items: string[] }
  | { type: "warning"; text: string }
  | { type: "note"; text: string }
  | { type: "tip"; text: string };

type InstructionCategory = "Аксессуары" | "Инсталляции" | "Смесители" | "Керамика";

type InstallationInstructionsJSON = {
  section: string;
  brand: string;
  title: string;
  description?: string;
  meta: { language: string; content_type: string; last_updated?: string };
  sections: Record<string, InstructionsBlock[]>;
  pdf_section?: {
    enabled: boolean;
    description?: string;
    documents: { title: string; category?: string; url: string; language?: string }[];
  };
};

// ===== Render helpers =====
type FlatRow =
  | { kind: "category"; id: string; title: string }
  | { kind: "block"; id: string; block: InstructionsBlock; category: string }
  | { kind: "pdf_stub"; id: string; title: string; description?: string };

const CATEGORY_ORDER: InstructionCategory[] = [
  "Аксессуары",
  "Инсталляции",
  "Смесители",
  "Керамика",
];

const getAccent = (t: "warning" | "note" | "tip") => {
  if (t === "warning") return { border: "#E11D48", bg: "#FFF1F2", text: "#9F1239", label: "Важно" };
  if (t === "tip") return { border: "#1F4FD8", bg: "#EFF6FF", text: "#1E40AF", label: "Совет" };
  return { border: "#94A3B8", bg: "#F8FAFC", text: "#334155", label: "Примечание" };
};

const BlockView: React.FC<{
  block: InstructionsBlock;
  scaleAll: number;
  brandBlue?: string;
}> = ({ block, scaleAll }) => {
  // headings
  if (block.type === "heading") {
    const size =
      block.level === 2 ? 16 : block.level === 3 ? 14 : 18;
    return (
      <Text
        fontSize={size * scaleAll}
        fontWeight="800"
        color="coolGray.800"
        mt={10 * scaleAll}
      >
        {block.text}
      </Text>
    );
  }

  // paragraph
  if (block.type === "paragraph") {
    return (
      <Text fontSize={13 * scaleAll} color="coolGray.700" lineHeight={19 * scaleAll}>
        {block.text}
      </Text>
    );
  }

  // list
  if (block.type === "list") {
    return (
      <VStack space={2} mt={2 * scaleAll}>
        {block.items.map((t, idx) => (
          <HStack key={idx} space={2} alignItems="flex-start">
            <Text fontSize={13 * scaleAll} color="coolGray.700" mt={1 * scaleAll}>
              {block.style === "numbered" ? `${idx + 1}.` : "•"}
            </Text>
            <Text flex={1} fontSize={13 * scaleAll} color="coolGray.700" lineHeight={19 * scaleAll}>
              {t}
            </Text>
          </HStack>
        ))}
      </VStack>
    );
  }

  // warning / note / tip
  if (block.type === "warning" || block.type === "note" || block.type === "tip") {
    const a = getAccent(block.type);
    return (
      <Box
        mt={4 * scaleAll}
        borderWidth={1}
        borderColor={a.border}
        bg={a.bg}
        borderRadius={12 * scaleAll}
        px={12 * scaleAll}
        py={10 * scaleAll}
      >
        <HStack justifyContent="space-between" alignItems="center" mb={2 * scaleAll}>
          <Text fontSize={12 * scaleAll} fontWeight="800" color={a.text}>
            {a.label}
          </Text>
          <Badge
            borderRadius={999}
            bg="transparent"
            borderWidth={1}
            borderColor={a.border}
            _text={{ color: a.text, fontWeight: "800", fontSize: 10 * scaleAll }}
          >
            {block.type.toUpperCase()}
          </Badge>
        </HStack>
        <Text fontSize={13 * scaleAll} color={a.text} lineHeight={19 * scaleAll}>
          {block.text}
        </Text>
      </Box>
    );
  }

  return null;
};

const CategoryHeader: React.FC<{ title: string; scaleAll: number; brandBlue?: string }> = ({
  title,
  scaleAll,
}) => {
  return (
    <Box mt={10 * scaleAll} mb={6 * scaleAll}>
      <HStack alignItems="center" justifyContent="space-between">
        <Text fontSize={18 * scaleAll} fontWeight="900" color="coolGray.900">
          {title}
        </Text>
        <Box height={1} flex={1} ml={10 * scaleAll} bg="coolGray.200" />
      </HStack>
    </Box>
  );
};

const PdfStubCard: React.FC<{
  title: string;
  description?: string;
  scaleAll: number;
  brandBlue: string;
}> = ({ title, description, scaleAll, brandBlue }) => {
  return (
    <Box
      mt={14 * scaleAll}
      borderWidth={1}
      borderColor="coolGray.200"
      bg="white"
      borderRadius={14 * scaleAll}
      px={14 * scaleAll}
      py={14 * scaleAll}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <VStack flex={1} space={1}>
          <Text fontSize={15 * scaleAll} fontWeight="900" color="coolGray.900">
            {title}
          </Text>
          {!!description && (
            <Text fontSize={12 * scaleAll} color="coolGray.600" numberOfLines={2}>
              {description}
            </Text>
          )}
        </VStack>

        <Box
          ml={10 * scaleAll}
          px={10 * scaleAll}
          py={6 * scaleAll}
          borderRadius={999}
          borderWidth={1}
          borderColor={brandBlue}
        >
          <Text fontSize={11 * scaleAll} fontWeight="700" color={brandBlue}>
            PDF
          </Text>
        </Box>
      </HStack>

      {/* <Text mt={10 * scaleAll} fontSize={12 * scaleAll} color="coolGray.500">
        (Раздел заготовлен — можно заполнить позже)
      </Text> */}
    </Box>
  );
};

const InstructionsBuild: React.FC<defaultNavigationProps> = ({ navigation, route }) => {
  const { instructionsData, scaleAll } = useAppContext();

  const brandBlue = "#1F4FD8"; // фирменный акцент (можешь заменить на ваш точный)

  const instructions: InstallationInstructionsJSON | null = instructionsData ?? null;

  const rows: FlatRow[] = useMemo(() => {
    if (!instructions?.sections) return [];

    const out: FlatRow[] = [];

    const sectionKeys = Object.keys(instructions.sections);
    const ordered = [
      ...CATEGORY_ORDER.filter((k) => sectionKeys.includes(k)),
      ...sectionKeys.filter((k) => !CATEGORY_ORDER.includes(k as InstructionCategory)),
    ];

    for (const cat of ordered) {
      out.push({ kind: "category", id: `cat:${cat}`, title: cat });

      const blocks = instructions.sections[cat] ?? [];
      for (let i = 0; i < blocks.length; i++) {
        out.push({
          kind: "block",
          id: `blk:${cat}:${i}`,
          block: blocks[i],
          category: cat,
        });
      }

      // если в категории пусто — можно показать маленький плейсхолдер
      if (blocks.length === 0) {
        out.push({
          kind: "block",
          id: `blk:${cat}:empty`,
          category: cat,
          block: {
            type: "note",
            text: "Информация будет добавлена позже.",
          },
        });
      }
    }

    // PDF stub внизу (даже если enabled=false — оставим как “заготовку”)
    if (instructions.pdf_section) {
      out.push({
        kind: "pdf_stub",
        id: "pdf_stub",
        title: "PDF инструкции",
        description: instructions.pdf_section.description,
      });
    } else {
      // даже если поля нет — можно добавить заглушку, чтобы ты “потом дополнил”
      out.push({
        kind: "pdf_stub",
        id: "pdf_stub",
        title: "PDF инструкции",
        description: "Раздел для PDF-инструкций по монтажу. Может быть заполнен позже.",
      });
    }

    return out;
  }, [instructions]);

  const renderItem = useCallback(
    ({ item }: { item: FlatRow }) => {
      // контент-ширина (чтобы на web не было “на весь экран”)
      const maxW = 980 * scaleAll;

      return (
        <Box px={12 * scaleAll} py={6 * scaleAll} alignItems="center">
          <Box width="100%" maxW={maxW}>
            {item.kind === "category" && <CategoryHeader title={item.title} scaleAll={scaleAll} />}
            {item.kind === "block" && (
              <Box mt={2 * scaleAll}>
                <BlockView block={item.block} scaleAll={scaleAll} brandBlue={brandBlue} />
              </Box>
            )}
            {/* {item.kind === "pdf_stub" && (
              <PdfStubCard
                title={item.title}
                description={item.description}
                scaleAll={scaleAll}
                brandBlue={brandBlue}
              />
            )} */}
          </Box>
        </Box>
      );
    },
    [scaleAll]
  );

  if (!instructions) {
    return (
      <Box px={14 * scaleAll} py={18 * scaleAll}>
        <Text fontSize={14 * scaleAll} color="coolGray.600">
          Инструкции пока не загружены.
        </Text>
      </Box>
    );
  }

  return (
    <FlashList
      data={rows}
      renderItem={renderItem}
      keyExtractor={(i) => i.id}
      estimatedItemSize={110 * scaleAll}
      showsVerticalScrollIndicator
      contentContainerStyle={{
        paddingBottom: 30 * scaleAll,
        paddingTop: 8 * scaleAll,
      }}
      ListHeaderComponent={
        <Box px={12 * scaleAll} pt={8 * scaleAll} pb={10 * scaleAll} alignItems="center">
          <Box width="100%" maxW={980 * scaleAll}>
            <Text fontSize={22 * scaleAll} fontWeight="900" color="coolGray.900">
              {instructions.title}
            </Text>
            {!!instructions.description && (
              <Text mt={6 * scaleAll} fontSize={13 * scaleAll} color="coolGray.600" lineHeight={19 * scaleAll}>
                {instructions.description}
              </Text>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default InstructionsBuild;
